import { useCallback, useEffect, useState } from 'react';
import { AlertCircle, CheckCircle2, Loader2, MessageSquare, Target } from 'lucide-react';
import { api } from '../../services/api';

export type ObjectiveStatus = 'awaiting_sponsor' | 'confirmed' | 'change_suggested';

export interface SessionObjective {
  text: string;
  status: ObjectiveStatus;
  updatedAt: string;
  respondedAt: string | null;
  suggestion: string | null;
}

export interface SessionHope {
  email: string;
  displayName: string | null;
  text: string;
  updatedAt: string;
}

export interface SessionAnswersData {
  objective: SessionObjective | null;
  hopes: SessionHope[];
}

const MAX = 1000;

const STATUS: Record<ObjectiveStatus, { label: string; cls: string }> = {
  awaiting_sponsor: { label: 'Waiting for the sponsor', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  confirmed: { label: 'Confirmed by the sponsor', cls: 'bg-green-50 text-green-600 border-green-200' },
  change_suggested: { label: 'Sponsor suggested a change', cls: 'bg-red-50 text-red-600 border-red-200' },
};

const errorOf = (e: any, fallback: string) => e?.response?.data?.error || e?.message || fallback;

export function ObjectiveStatusChip({ status }: { status: ObjectiveStatus }) {
  return <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${STATUS[status].cls}`}>{STATUS[status].label}</span>;
}

function Suggestion({ objective }: { objective: SessionObjective }) {
  if (objective.status !== 'change_suggested' || !objective.suggestion) return null;
  return (
    <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2 whitespace-pre-line">
      <span className="font-semibold">Suggested change:</span> {objective.suggestion}
    </p>
  );
}

/** The attendees' "what would make this worthwhile?" answers. Visible to Version 1 staff only. */
export function HopesList({ hopes }: { hopes: SessionHope[] }) {
  if (hopes.length === 0) return <p className="text-xs text-slate-400">No answers yet.</p>;
  return (
    <ul className="flex flex-col divide-y divide-[#e2e8f0] border border-[#e2e8f0] rounded-xl bg-white">
      {hopes.map((h) => (
        <li key={h.email} className="px-3 py-2">
          <p className="text-xs font-semibold text-navy">{h.displayName || h.email}</p>
          <p className="text-sm text-slate-600 whitespace-pre-line">{h.text}</p>
        </li>
      ))}
    </ul>
  );
}

/** Read only, for the account manager's pack drawer. */
export function SessionAnswersReadOnly({ answers }: { answers: SessionAnswersData }) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1.5">
        <p className="text-xs font-semibold text-slate-500 flex items-center gap-1.5"><Target size={13} /> Objective</p>
        {answers.objective ? (
          <>
            <p className="text-sm text-slate-700 whitespace-pre-line">{answers.objective.text}</p>
            <div><ObjectiveStatusChip status={answers.objective.status} /></div>
            <Suggestion objective={answers.objective} />
          </>
        ) : (
          <p className="text-xs text-slate-400">The facilitator has not set an objective yet.</p>
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        <p className="text-xs font-semibold text-slate-500 flex items-center gap-1.5"><MessageSquare size={13} /> What would make it worthwhile</p>
        <HopesList hopes={answers.hopes} />
      </div>
    </div>
  );
}

/** The facilitator writes the objective; the sponsor confirms it in the hub. */
export function ObjectiveAndHopes({ projectId, sessionId, canManage }: { projectId: string; sessionId: string; canManage: boolean }) {
  const base = `/api/projects/${projectId}/session-hub/sessions/${sessionId}`;
  const [data, setData] = useState<SessionAnswersData | null>(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = (await api.get<SessionAnswersData>(`${base}/before`)).data;
      setData(r);
      setText(r.objective?.text ?? '');
    } catch (e) {
      setError(errorOf(e, 'Could not load the objective'));
    }
  }, [base]);
  useEffect(() => { load(); }, [load]);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const r = (await api.put<{ objective: SessionObjective }>(`${base}/objective`, { text })).data;
      setData((d) => ({ hopes: d?.hopes ?? [], objective: r.objective }));
      setText(r.objective.text);
    } catch (e) {
      setError(errorOf(e, 'Could not save the objective'));
    } finally {
      setBusy(false);
    }
  }

  if (!data) return error ? <p className="text-xs text-red-700">{error}</p> : <Loader2 size={16} className="animate-spin text-slate-400" />;
  const changed = text.trim() !== (data.objective?.text ?? '');

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-[11px] font-bold text-primary tracking-widest uppercase">Objective</p>
          {data.objective && <ObjectiveStatusChip status={data.objective.status} />}
        </div>
        {canManage ? (
          <>
            <textarea
              aria-label="Session objective" rows={3} maxLength={MAX} value={text} onChange={(e) => setText(e.target.value)}
              placeholder="What the session should achieve, in the client's terms. The sponsor confirms it in the hub."
              className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none bg-white"
            />
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-slate-400">{data.objective ? 'Changing the wording asks the sponsor to confirm again.' : 'Attendees see it on the session page; only the sponsor can confirm it.'}</p>
              <button type="button" disabled={busy || !changed || !text.trim()} onClick={save}
                className="flex items-center gap-1.5 rounded-lg h-8 px-3 bg-primary hover:bg-primary-dark text-white text-xs font-bold disabled:opacity-60">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Save objective
              </button>
            </div>
          </>
        ) : (
          <p className="text-sm text-slate-700 whitespace-pre-line">{data.objective?.text ?? 'No objective yet.'}</p>
        )}
        {data.objective && <Suggestion objective={data.objective} />}
        {error && <p className="text-xs font-medium text-red-700 flex items-center gap-1.5"><AlertCircle size={14} /> {error}</p>}
      </div>
      <div className="flex flex-col gap-2">
        <p className="text-[11px] font-bold text-primary tracking-widest uppercase">What would make it worthwhile</p>
        <p className="text-xs text-slate-400">Each attendee's answer from the hub. Seen by facilitators and the account manager, never by other attendees.</p>
        <HopesList hopes={data.hopes} />
      </div>
    </div>
  );
}
